// Performance optimization utilities
export class PerformanceOptimizer {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
    this.retryCount = new Map();
    this.maxRetries = 3;
    this.retryDelay = 1000; // ms
  }

  // Cache API responses to reduce duplicate requests
  async cachedApiGet(url, cacheDuration = 300000) { // 5 min default
    const cacheKey = url;
    const now = Date.now();
    
    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && (now - cached.timestamp) < cacheDuration) {
      return cached.data;
    }

    // Check if request is already pending
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    // Make request with retry logic
    const requestPromise = this.fetchWithRetry(url);
    this.pendingRequests.set(cacheKey, requestPromise);

    try {
      const data = await requestPromise;
      this.cache.set(cacheKey, { data, timestamp: now });
      this.retryCount.delete(url);
      return data;
    } catch (error) {
      throw error;
    } finally {
      this.pendingRequests.delete(cacheKey);
    }
  }

  // Fetch with exponential backoff retry
  async fetchWithRetry(url, options = {}) {
    const currentRetries = this.retryCount.get(url) || 0;
    
    try {
      const response = await fetch(url, { 
        credentials: 'include',
        ...options 
      });

      if (!response.ok) {
        if (response.status >= 500 && currentRetries < this.maxRetries) {
          // Server error - retry
          this.retryCount.set(url, currentRetries + 1);
          await this.delay(this.retryDelay * Math.pow(2, currentRetries));
          return this.fetchWithRetry(url, options);
        }
        
        // Client error or max retries reached
        const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
        throw errorData;
      }

      return await response.json();
    } catch (error) {
      if (currentRetries < this.maxRetries && 
          (error.name === 'TypeError' || error.message.includes('fetch'))) {
        // Network error - retry
        this.retryCount.set(url, currentRetries + 1);
        await this.delay(this.retryDelay * Math.pow(2, currentRetries));
        return this.fetchWithRetry(url, options);
      }
      throw error;
    }
  }

  // Batch multiple API requests with concurrency limit
  async batchRequests(urls, concurrency = 3) {
    const results = [];
    const executing = [];

    for (const url of urls) {
      const promise = this.cachedApiGet(url).then(
        data => ({ url, data, error: null }),
        error => ({ url, data: null, error })
      );
      
      results.push(promise);
      
      if (urls.length >= concurrency) {
        executing.push(promise);
        
        if (executing.length >= concurrency) {
          await Promise.race(executing);
          executing.splice(executing.findIndex(p => p === promise), 1);
        }
      }
    }

    return Promise.all(results);
  }

  // Lazy load sections on scroll/interaction
  setupLazyLoading() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;
          const loadFunction = element.dataset.lazyLoad;
          if (loadFunction && window[loadFunction]) {
            window[loadFunction]();
            observer.unobserve(element);
          }
        }
      });
    }, { threshold: 0.1, rootMargin: '100px' });

    document.querySelectorAll('[data-lazy-load]').forEach(el => {
      observer.observe(el);
    });
  }

  // Debounce function for search input
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Clear cache periodically to prevent memory leaks
  startCacheCleanup() {
    setInterval(() => {
      const now = Date.now();
      for (const [key, value] of this.cache.entries()) {
        if (now - value.timestamp > 600000) { // 10 minutes
          this.cache.delete(key);
        }
      }
    }, 300000); // Clean every 5 minutes
  }
}

// Global performance optimizer instance
export const perfOpt = new PerformanceOptimizer();

// Enhanced loading states
export function showLoadingSpinner(containerId, message = 'Loading...') {
  const container = document.getElementById(containerId);
  if (container) {
    container.innerHTML = `
      <div class="loading-state">
        <div class="spinner"></div>
        <p>${message}</p>
      </div>
    `;
  }
}

export function showErrorState(containerId, error, retryFunction = null) {
  const container = document.getElementById(containerId);
  if (container) {
    const retryButton = retryFunction ? 
      `<button class="btn-retry" onclick="${retryFunction}">Try Again</button>` : '';
    
    container.innerHTML = `
      <div class="error-state">
        <p class="error-message">${error.error || error.message || 'Something went wrong'}</p>
        ${retryButton}
      </div>
    `;
  }
}

// Preload critical resources
export function preloadCriticalData(bbl) {
  const criticalEndpoints = [
    `/api/property?bbl=${bbl}`,
    `/api/building-profile?bbl=${bbl}`
  ];

  // Start loading critical data immediately
  criticalEndpoints.forEach(url => {
    perfOpt.cachedApiGet(url).catch(() => {}); // Silent fail for preload
  });
}